import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isManagedReadOnly, getManagedReadOnlyMessage, canResumeHumanAttention } from '../../utils/whatsappLifecycle';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabaseClient';
import {
    MessageSquare,
    Search,
    Loader2,
    AlertCircle,
    Filter,
    X,
    Calendar,
    User,
    Tag as TagIcon,
    Phone,
    Eye,
    RefreshCw,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    Bell,
    CheckSquare,
    Square,
    Users,
    Archive,
    CheckCircle,
    Download,
    FileSpreadsheet,
    Trash2,
    Clock,
    CheckCircle2,
    FileText
} from 'lucide-react';
import ChannelBadge from '../ChannelBadge';
import ObjectTags from '../common/ObjectTags';
import WhatsAppAttachmentItem from '../WhatsAppAttachmentItem';
import CopyablePhone from '../CopyablePhone';

const WhatsAppSupervisorView = () => {
    const { authFetch } = useAuth();

    // State for conversations list
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [limit] = useState(50);
    const [offset, setOffset] = useState(0);

    // Filters state
    const [filters, setFilters] = useState({
        from_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        agent_id: '',
        role_id: '',
        tag_id: '',
        search: '',
        conversation_filter: 'all',
        en_gestion_filter: 'all'
    });

    // Sorting state
    const [sortBy, setSortBy] = useState('updated_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Filter options
    const [roles, setRoles] = useState([]);
    const [agents, setAgents] = useState([]);
    const [tags, setTags] = useState([]);

    // Selected conversation for messages panel
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);

    // Bulk selection state
    const [selectedConversations, setSelectedConversations] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [bulkAssigningAgent, setBulkAssigningAgent] = useState(false);
    const [bulkAssigningTag, setBulkAssigningTag] = useState(false);

    // Export state
    const [showExportsModal, setShowExportsModal] = useState(false);
    const [exportsList, setExportsList] = useState([]);
    const [exportInProgress, setExportInProgress] = useState(false);
    const [exportMessagesInProgress, setExportMessagesInProgress] = useState(false);
    const [loadingExports, setLoadingExports] = useState(false);

    // Toast notifications
    const [toasts, setToasts] = useState([]);

    // Realtime subscription
    const conversationsChannelRef = useRef(null);
    const messagesChannelRef = useRef(null);
    const exportsChannelRef = useRef(null);

    // Refs to read latest state without recreating callbacks
    const filtersRef = useRef(filters);
    const offsetRef = useRef(offset);
    const abortControllerRef = useRef(null);

    useEffect(() => { filtersRef.current = filters; }, [filters]);
    useEffect(() => { offsetRef.current = offset; }, [offset]);

    // Fetch filter options
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const [rolesRes, agentsRes, tagsRes] = await Promise.all([
                    authFetch('/api/admin/roles'),
                    authFetch('/api/backoffice/users'),
                    authFetch('/api/tags/?all=true')
                ]);

                if (rolesRes.ok) {
                    const rolesData = await rolesRes.json();
                    setRoles(rolesData.roles || rolesData || []);
                }

                if (agentsRes.ok) {
                    const agentsData = await agentsRes.json();
                    setAgents(agentsData.users || agentsData || []);
                }

                if (tagsRes.ok) {
                    const tagsData = await tagsRes.json();
                    setTags(tagsData || []);
                }
            } catch (err) {
                console.error('Error fetching filter options:', err);
            }
        };

        fetchFilterOptions();
    }, [authFetch]);

    // Fetch conversations - reads latest filters/offset from refs to avoid recreating the callback
    const fetchConversations = useCallback(async (customOffset = null) => {
        // Cancel any in-flight request to avoid stale responses overwriting fresh data
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setError(null);

        try {
            const currentFilters = filtersRef.current;
            const effectiveOffset = customOffset !== null ? customOffset : offsetRef.current;

            // Transform conversation_filter to backend parameters
            const filterParams = Object.fromEntries(
                Object.entries(currentFilters).filter(([key, v]) => {
                    // Excluir valores vacíos, 'all' values y conversation_filter
                    if (v === '') return false;
                    if (key === 'en_gestion_filter' && v === 'all') return false;
                    if (key === 'conversation_filter') return false;
                    return true;
                })
            );

            // Map conversation_filter to legacy backend parameters
            if (currentFilters.conversation_filter === 'unassigned') {
                filterParams.unassigned = 'true';
            } else if (currentFilters.conversation_filter === 'inbound_unanswered') {
                filterParams.inbound_unanswered = 'true';
            } else if (currentFilters.conversation_filter === 'inbound_only') {
                filterParams.inbound_only = 'true';
            } else if (currentFilters.conversation_filter === 'outbound_only') {
                filterParams.outbound_only = 'true';
            }

            const params = new URLSearchParams({
                limit: limit.toString(),
                offset: effectiveOffset.toString(),
                sort_by: sortBy,
                sort_order: sortOrder,
                ...filterParams
            });

            const response = await authFetch(`/api/backoffice/whatsapp/conversations-supervisor?${params}`, {
                signal: abortController.signal
            });

            if (abortController.signal.aborted) {
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch conversations');
            }

            const data = await response.json();
            setConversations(data.conversations || []);
            setTotal(data.total || 0);
        } catch (err) {
            if (err.name === 'AbortError') {
                return;
            }
            console.error('Error fetching conversations:', err);
            setError(err.message);
        } finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }
            setLoading(false);
        }
    }, [authFetch, limit, sortBy, sortOrder]); // filters/offset removed from dependencies

    // Fetch messages for selected conversation
    const fetchMessages = useCallback(async (conversationId) => {
        setLoadingMessages(true);
        try {
            const response = await authFetch(`/api/whatsapp/messages/${conversationId}`);
            if (response.ok) {
                const data = await response.json();
                setMessages(data || []);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
        } finally {
            setLoadingMessages(false);
        }
    }, [authFetch]);

    // Handle conversation selection
    const handleSelectConversation = (conversation) => {
        setSelectedConversation(conversation);
        fetchMessages(conversation.conversation_id);
    };

    // Handle attachment download
    const handleDownloadAttachment = async (att) => {
        try {
            const url = `/api/whatsapp/download/${att.id}`;
            const response = await authFetch(url);
            if (!response.ok) throw new Error('Download failed');
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            
            const disposition = response.headers.get('content-disposition');
            let fileName = `whatsapp_file_${att.id}`;
            
            if (disposition) {
                const fileNameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/['"]/g, '');
                }
            } else if (att.filename || att.original_filename) {
                fileName = att.filename || att.original_filename;
            }

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('[Backoffice] Error downloading attachment:', error);
            alert('Error al descargar el archivo');
        }
    };

    // Handle column sort
    const handleSort = (column) => {
        if (sortBy === column) {
            // Toggle order if same column
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            // New column, default to desc
            setSortBy(column);
            setSortOrder('desc');
        }
    };

    // Apply filters
    const handleApplyFilters = () => {
        setOffset(0);
        // Trigger fetch explicitly so the filters are applied even when offset is already 0.
        fetchConversations(0);
    };

    // Clear filters
    const handleClearFilters = () => {
        const clearedFilters = {
            from_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            to_date: new Date().toISOString().split('T')[0],
            agent_id: '',
            role_id: '',
            tag_id: '',
            search: '',
            conversation_filter: 'all',
            en_gestion_filter: 'all'
        };
        setFilters(clearedFilters);
        filtersRef.current = clearedFilters;
        setOffset(0);
        fetchConversations(0);
    };

    // Refetch when sort changes - reset to page 1
    useEffect(() => {
        setOffset(0);
        fetchConversations(0);
    }, [sortBy, sortOrder, fetchConversations]);

    // Setup realtime subscription for conversations
    useEffect(() => {
        const channel = supabase
            .channel('supervisor-conversations')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'messaging',
                table: 'conversations'
            }, () => {
                // Refresh conversations using the latest filters without recreating subscription
                fetchConversations();
            })
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'messaging',
                table: 'conversations'
            }, (payload) => {
                // Update specific conversation in list
                setConversations(prev => prev.map(conv =>
                    conv.conversation_id === payload.new.id
                        ? { ...conv, ...payload.new }
                        : conv
                ));
            })
            .on('postgres_changes', { event: '*', schema: 'espuma', table: 'whatsapp_bot_return_requests' }, () => fetchConversations())
            .on('postgres_changes', { event: '*', schema: 'messaging', table: 'tenant_channel_decision_flows' }, () => fetchConversations())
            .subscribe();

        conversationsChannelRef.current = channel;

        return () => {
            channel.unsubscribe();
        };
    }, []); // stable subscription, reads latest filters via refs

    // Setup realtime subscription for messages (when conversation is selected)
    useEffect(() => {
        if (!selectedConversation) {
            if (messagesChannelRef.current) {
                messagesChannelRef.current.unsubscribe();
                messagesChannelRef.current = null;
            }
            return;
        }

        const channel = supabase
            .channel(`supervisor-messages-${selectedConversation.conversation_id}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'messaging',
                table: 'messages',
                filter: `conversation_id=eq.${selectedConversation.conversation_id}`
            }, (payload) => {
                setMessages(prev => [...prev, payload.new].sort((a, b) => 
                    new Date(a.created_at) - new Date(b.created_at)
                ));
            })
            .subscribe();

        messagesChannelRef.current = channel;

        return () => {
            channel.unsubscribe();
        };
    }, [selectedConversation]);

    // Pagination handlers
    const handlePreviousPage = () => {
        if (offset > 0) {
            setOffset(prev => Math.max(0, prev - limit));
        }
    };

    const handleNextPage = () => {
        if (offset + limit < total) {
            setOffset(prev => prev + limit);
        }
    };

    // Effect to fetch when offset changes
    useEffect(() => {
        fetchConversations(offset);
    }, [offset]);

    // Bulk selection handlers
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedConversations([]);
            setSelectAll(false);
        } else {
            setSelectedConversations(conversations.map(c => c.conversation_id));
            setSelectAll(true);
        }
    };

    const toggleConversationSelection = (convId) => {
        setSelectedConversations(prev => {
            if (prev.includes(convId)) {
                return prev.filter(id => id !== convId);
            } else {
                return [...prev, convId];
            }
        });
    };

    const handleBulkAssignAgent = async (agentEmail) => {
        if (selectedConversations.length === 0) return;
        
        setBulkAssigningAgent(true);
        try {
            const response = await authFetch('/api/backoffice/whatsapp/bulk-assign-agent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversation_ids: selectedConversations,
                    agent_email: agentEmail
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al asignar agente');
            }

            const data = await response.json();
            alert(data.message || 'Agente asignado correctamente');
            
            // Refresh conversations and clear selection
            fetchConversations(offset);
            setSelectedConversations([]);
            setSelectAll(false);
        } catch (err) {
            console.error('Error in bulk assign agent:', err);
            alert(err.message || 'Error al asignar agente masivamente');
        } finally {
            setBulkAssigningAgent(false);
        }
    };

    const handleBulkAssignTag = async (tagId) => {
        if (selectedConversations.length === 0) return;
        
        setBulkAssigningTag(true);
        try {
            const response = await authFetch('/api/backoffice/whatsapp/bulk-assign-tag', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversation_ids: selectedConversations,
                    tag_id: tagId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al asignar etiqueta');
            }

            const data = await response.json();
            alert(data.message || 'Etiqueta asignada correctamente');
            
            // Refresh conversations and clear selection
            fetchConversations(offset);
            setSelectedConversations([]);
            setSelectAll(false);
        } catch (err) {
            console.error('Error in bulk assign tag:', err);
            alert(err.message || 'Error al asignar etiqueta masivamente');
        } finally {
            setBulkAssigningTag(false);
        }
    };

    const handleRemoveTag = async (conversationId, tagId, e) => {
        // Prevent row click event
        if (e) e.stopPropagation();
        
        try {
            const response = await authFetch('/api/tags/assign', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tag_id: tagId,
                    object_type: 'whatsapp_conversation',
                    object_id: conversationId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al eliminar etiqueta');
            }

            // Refresh conversations to reflect changes
            fetchConversations(offset);
        } catch (err) {
            console.error('Error removing tag:', err);
            alert(err.message || 'Error al eliminar etiqueta');
        }
    };

    const handleToggleEnGestion = async (conversationId, currentValue, e) => {
        // Prevent row click event
        if (e) e.stopPropagation();
        
        try {
            const conversation = conversations.find(conv => conv.conversation_id === conversationId);
            const resumeHuman = canResumeHumanAttention(conversation);
            const newValue = resumeHuman || !currentValue;
            
            // Optimistic update
            setConversations(prev => prev.map(conv => 
                conv.conversation_id === conversationId 
                    ? { ...conv, en_gestion: newValue }
                    : conv
            ));
            
            const response = await authFetch(
                `/api/whatsapp/conversation/${conversationId}/${resumeHuman ? 'resume-human' : 'en-gestion'}`,
                {
                    method: resumeHuman ? 'POST' : 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ en_gestion: newValue })
                }
            );

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || body.message || 'Error al actualizar estado');
            }

            const result = await response.json();
            setConversations(prev => prev.map(conv => conv.conversation_id === conversationId
                ? { ...conv, en_gestion: result.en_gestion ?? newValue,
                    bot_return_pending: result.bot_return_pending || false,
                    can_operate: result.can_operate ?? (result.bot_return_pending ? false : conv.can_operate),
                    mode: result.mode ?? conv.mode, status: result.status ?? conv.status }
                : conv));
            if (result.bot_return_pending) {
                showToast('info', 'Devolución al bot pendiente', 'Se reintentará automáticamente.');
                fetchConversations();
                return;
            }
            fetchConversations();
            if (result.bot_mode_updated === false) {
                showToast(
                    'error',
                    'Bot no activado',
                    result.warning || 'La conversación se archivó, pero no se pudo activar el bot en WhatsApp Hub.'
                );
            }

            console.log(`[SupervisorView] Conversación ${conversationId} → en_gestion=${newValue}`);
            
        } catch (err) {
            console.error('[SupervisorView] Error toggling en_gestion:', err);
            // Revert optimistic update
            setConversations(prev => prev.map(conv => 
                conv.conversation_id === conversationId 
                    ? { ...conv, en_gestion: currentValue }
                    : conv
            ));
            alert(err.message || 'Error al cambiar estado de gestión');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleString('es-ES', { 
            day: '2-digit', 
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const formatDateShort = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        const now = new Date();
        const diff = now - d;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        
        if (hours < 1) {
            const minutes = Math.floor(diff / (1000 * 60));
            return `Hace ${minutes}m`;
        }
        if (hours < 24) {
            return `Hace ${hours}h`;
        }
        const days = Math.floor(hours / 24);
        if (days < 7) {
            return `Hace ${days}d`;
        }
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    };

    const getTimeAgo = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        const now = new Date();
        const diff = now - d;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        
        if (minutes < 1) return 'hace menos de 1m';
        if (minutes < 60) return `hace ${minutes}m`;
        if (hours < 24) return `hace ${hours}h`;
        if (days < 7) return `hace ${days}d`;
        if (days < 30) {
            const weeks = Math.floor(days / 7);
            return `hace ${weeks} sem`;
        }
        if (days < 365) {
            const months = Math.floor(days / 30);
            return `hace ${months} ${months === 1 ? 'mes' : 'meses'}`;
        }
        const years = Math.floor(days / 365);
        return `hace ${years} ${years === 1 ? 'año' : 'años'}`;
    };

    // Export handlers

    // Construye los filtros a enviar al backend a partir de los filtros aplicados en pantalla
    const buildExportFilters = () => {
        const exportFilters = {
            from_date: filters.from_date ? `${filters.from_date}T00:00:00` : undefined,
            to_date: filters.to_date ? `${filters.to_date}T23:59:59` : undefined,
            agent_id: filters.agent_id || undefined,
            role_id: filters.role_id || undefined,
            tag_id: filters.tag_id || undefined,
            search: filters.search || undefined,
            en_gestion_filter: filters.en_gestion_filter !== 'all' ? filters.en_gestion_filter : undefined
        };

        // Mapear conversation_filter a parámetros del backend
        if (filters.conversation_filter === 'unassigned') {
            exportFilters.unassigned = true;
        } else if (filters.conversation_filter === 'inbound_unanswered') {
            exportFilters.inbound_unanswered = true;
        } else if (filters.conversation_filter === 'inbound_only') {
            exportFilters.inbound_only = true;
        } else if (filters.conversation_filter === 'outbound_only') {
            exportFilters.outbound_only = true;
        }

        // Limpiar valores undefined
        Object.keys(exportFilters).forEach(key => {
            if (exportFilters[key] === undefined) {
                delete exportFilters[key];
            }
        });

        return exportFilters;
    };

    const handleExport = async () => {
        if (exportInProgress) return;

        try {
            setExportInProgress(true);

            const exportFilters = buildExportFilters();
            exportFilters.export_type = 'conversations';

            const response = await authFetch('/api/backoffice/whatsapp/conversations-supervisor/export', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(exportFilters)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al iniciar exportación');
            }

            const data = await response.json();
            
            showToast('success', 'Exportación iniciada', 'Se te notificará cuando esté listo para descargar');
            
            // Abrir modal de exports automáticamente
            setShowExportsModal(true);
            fetchExports();

        } catch (err) {
            console.error('Error creating export:', err);
            showToast('error', 'Error', err.message || 'Error al crear exportación');
        } finally {
            setExportInProgress(false);
        }
    };

    const handleExportMessages = async () => {
        if (exportMessagesInProgress) return;

        try {
            setExportMessagesInProgress(true);

            const exportFilters = buildExportFilters();
            exportFilters.export_type = 'messages';

            const response = await authFetch('/api/backoffice/whatsapp/conversations-supervisor/export', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(exportFilters)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al iniciar exportación de mensajes');
            }

            const data = await response.json();

            showToast('success', 'Exportación de mensajes iniciada', 'Se te notificará cuando esté lista para descargar');

            // Abrir modal de exports automáticamente
            setShowExportsModal(true);
            fetchExports();

        } catch (err) {
            console.error('Error creating messages export:', err);
            showToast('error', 'Error', err.message || 'Error al crear exportación de mensajes');
        } finally {
            setExportMessagesInProgress(false);
        }
    };

    const fetchExports = async () => {
        try {
            setLoadingExports(true);
            const response = await authFetch('/api/backoffice/whatsapp/exports');
            
            if (!response.ok) {
                throw new Error('Error al obtener exportaciones');
            }

            const data = await response.json();
            setExportsList(data || []);
        } catch (err) {
            console.error('Error fetching exports:', err);
        } finally {
            setLoadingExports(false);
        }
    };

    const handleDownloadExport = async (exportId, filename) => {
        try {
            const response = await authFetch(`/api/backoffice/whatsapp/exports/${exportId}/download`);
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al descargar export');
            }

            // Descargar archivo
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `export_${exportId}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Error downloading export:', err);
            alert(err.message || 'Error al descargar archivo');
        }
    };

    const handleDeleteExport = async (exportId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta exportación?')) {
            return;
        }

        try {
            const response = await authFetch(`/api/backoffice/whatsapp/exports/${exportId}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al eliminar export');
            }

            // Actualizar lista
            fetchExports();
        } catch (err) {
            console.error('Error deleting export:', err);
            alert(err.message || 'Error al eliminar exportación');
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: { color: 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50', icon: Clock, label: 'Pendiente' },
            processing: { color: 'bg-blue-900/30 text-blue-300 border-blue-700/50', icon: Loader2, label: 'Procesando' },
            completed: { color: 'bg-green-900/30 text-green-300 border-green-700/50', icon: CheckCircle2, label: 'Completado' },
            failed: { color: 'bg-red-900/30 text-red-300 border-red-700/50', icon: AlertCircle, label: 'Error' }
        };

        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium ${badge.color}`}>
                <Icon size={12} className={status === 'processing' ? 'animate-spin' : ''} />
                {badge.label}
            </span>
        );
    };

    // Toast notification system
    const showToast = (type, title, message) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, type, title, message }]);
        
        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // Auto-dismiss toasts
    const dismissToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    // Fetch exports when modal is opened
    useEffect(() => {
        if (showExportsModal) {
            fetchExports();
        }
    }, [showExportsModal, authFetch]);

    // Subscribe to exports realtime updates
    useEffect(() => {
        const setupExportsSubscription = async () => {
            try {
                // Obtener el user_id del usuario actual (sub del token JWT)
                const { data: { user } } = await supabase.auth.getUser();
                
                if (!user) {
                    console.log('[SupervisorView] No user found for exports subscription');
                    return;
                }

                const channel = supabase
                    .channel('whatsapp-exports-changes')
                    .on(
                        'postgres_changes',
                        {
                            event: '*',
                            schema: 'espuma',
                            table: 'whatsapp_exports',
                            filter: `created_by=eq.${user.email}`
                        },
                        (payload) => {
                            console.log('[SupervisorView] Export change detected:', payload);
                            
                            if (payload.eventType === 'UPDATE' && payload.new.status === 'completed') {
                                // Mostrar notificación cuando un export se completa
                                showToast('success', '¡Exportación lista!', 'Tu exportación está lista para descargar');
                            }
                            
                            // Refrescar lista de exports si el modal está abierto
                            if (showExportsModal) {
                                fetchExports();
                            }
                        }
                    )
                    .subscribe();

                exportsChannelRef.current = channel;
            } catch (err) {
                console.error('[SupervisorView] Error setting up exports subscription:', err);
            }
        };

        setupExportsSubscription();

        return () => {
            if (exportsChannelRef.current) {
                supabase.removeChannel(exportsChannelRef.current);
            }
        };
    }, [showExportsModal]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center bg-slate-900/50 p-6 rounded-2xl border border-slate-800">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MessageSquare className="text-primary" />
                        Supervisión de Conversaciones WhatsApp
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Vista completa de todas las conversaciones con filtrado avanzado
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Export Button */}
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-600/30 rounded-lg text-sm transition-colors"
                        disabled={exportInProgress || loading}
                    >
                        <Download size={16} className={exportInProgress ? 'animate-bounce' : ''} />
                        {exportInProgress ? 'Exportando...' : 'Exportar'}
                    </button>

                    {/* Export Messages Button */}
                    <button
                        onClick={handleExportMessages}
                        className="flex items-center gap-2 px-4 py-2 bg-sky-600/20 hover:bg-sky-600/30 text-sky-400 border border-sky-600/30 rounded-lg text-sm transition-colors"
                        disabled={exportMessagesInProgress || loading}
                        title="Exporta todos los mensajes de las conversaciones filtradas"
                    >
                        <FileText size={16} className={exportMessagesInProgress ? 'animate-bounce' : ''} />
                        {exportMessagesInProgress ? 'Exportando...' : 'Exportar mensajes'}
                    </button>
                    
                    {/* View Exports Button */}
                    <button
                        onClick={() => setShowExportsModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-sm transition-colors"
                    >
                        <FileSpreadsheet size={16} />
                        Ver Exportaciones
                    </button>
                    
                    {/* Refresh Button */}
                    <button
                        onClick={() => fetchConversations()}
                        className="flex items-center gap-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg text-sm transition-colors"
                        disabled={loading}
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedConversations.length > 0 && (
                <div className="bg-blue-900/30 border border-blue-700/50 p-4 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <CheckSquare className="text-blue-400" size={20} />
                        <span className="text-blue-200 font-medium">
                            {selectedConversations.length} conversación{selectedConversations.length !== 1 ? 'es' : ''}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Assign Agent */}
                        <div className="flex items-center gap-2">
                            <User size={16} className="text-slate-400" />
                            <select
                                className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleBulkAssignAgent(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                                disabled={bulkAssigningAgent}
                            >
                                <option value="">Asignar agente...</option>
                                {agents.map(agent => (
                                    <option key={agent.id} value={agent.email}>
                                        {agent.full_name || agent.email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Assign Tag */}
                        <div className="flex items-center gap-2">
                            <TagIcon size={16} className="text-slate-400" />
                            <select
                                className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleBulkAssignTag(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                                disabled={bulkAssigningTag}
                            >
                                <option value="">Asignar etiqueta...</option>
                                {tags.map(tag => (
                                    <option key={tag.id} value={tag.id}>
                                        {tag.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Clear Selection */}
                        <button
                            onClick={() => {
                                setSelectedConversations([]);
                                setSelectAll(false);
                            }}
                            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm transition-colors"
                        >
                            Limpiar selección
                        </button>
                    </div>
                </div>
            )}

            {/* Main Layout: 3 columns */}
            <div className="flex gap-6 h-[calc(100vh-240px)]">
                {/* Left Sidebar: Filters */}
                <div className="w-80 bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col gap-4 overflow-y-auto">
                    <div className="flex items-center gap-2 mb-2">
                        <Filter size={18} className="text-slate-400" />
                        <h3 className="font-bold">Filtros</h3>
                    </div>

                    {/* Date Range */}
                    <div>
                        <label className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                            <Calendar size={14} />
                            Desde
                        </label>
                        <input
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => setFilters(prev => ({ ...prev, from_date: e.target.value }))}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                        />
                    </div>

                    <div>
                        <label className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                            <Calendar size={14} />
                            Hasta
                        </label>
                        <input
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => setFilters(prev => ({ ...prev, to_date: e.target.value }))}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                        />
                    </div>

                    {/* Role Filter */}
                    <div>
                        <label className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                            <User size={14} />
                            Rol / Equipo
                        </label>
                        <select
                            value={filters.role_id}
                            onChange={(e) => setFilters(prev => ({ ...prev, role_id: e.target.value }))}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                        >
                            <option value="">Todos los roles</option>
                            {roles.map(role => (
                                <option key={role.id} value={role.id}>{role.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Agent Filter */}
                    <div>
                        <label className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                            <User size={14} />
                            Agente Asignado
                        </label>
                        <select
                            value={filters.agent_id}
                            onChange={(e) => setFilters(prev => ({ ...prev, agent_id: e.target.value }))}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                            disabled={filters.conversation_filter === 'unassigned'}
                        >
                            <option value="">Todos los agentes</option>
                            {agents.map(agent => (
                                <option key={agent.id} value={agent.email}>
                                    {agent.full_name || agent.email}
                                </option>
                            ))}
                        </select>
                        
                        {/* Conversation Type Filter - Radio Buttons */}
                        <div className="mt-3 space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="conversation_filter"
                                    value="all"
                                    checked={filters.conversation_filter === 'all'}
                                    onChange={(e) => setFilters(prev => ({ 
                                        ...prev, 
                                        conversation_filter: e.target.value,
                                        agent_id: prev.agent_id
                                    }))}
                                    className="w-4 h-4 text-primary bg-slate-950 border-slate-700 focus:ring-primary focus:ring-2"
                                />
                                <span className="text-xs text-slate-300">Todas</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="conversation_filter"
                                    value="unassigned"
                                    checked={filters.conversation_filter === 'unassigned'}
                                    onChange={(e) => setFilters(prev => ({ 
                                        ...prev, 
                                        conversation_filter: e.target.value,
                                        agent_id: ''
                                    }))}
                                    className="w-4 h-4 text-primary bg-slate-950 border-slate-700 focus:ring-primary focus:ring-2"
                                />
                                <span className="text-xs text-slate-300">Solo sin agente asignado</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="conversation_filter"
                                    value="inbound_unanswered"
                                    checked={filters.conversation_filter === 'inbound_unanswered'}
                                    onChange={(e) => setFilters(prev => ({ 
                                        ...prev, 
                                        conversation_filter: e.target.value
                                    }))}
                                    className="w-4 h-4 text-orange-500 bg-slate-950 border-slate-700 focus:ring-orange-500 focus:ring-2"
                                />
                                <Bell size={12} className="text-orange-400" />
                                <span className="text-xs text-slate-300">Solo inbound sin respuesta</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="conversation_filter"
                                    value="inbound_only"
                                    checked={filters.conversation_filter === 'inbound_only'}
                                    onChange={(e) => setFilters(prev => ({ 
                                        ...prev, 
                                        conversation_filter: e.target.value
                                    }))}
                                    className="w-4 h-4 text-blue-500 bg-slate-950 border-slate-700 focus:ring-blue-500 focus:ring-2"
                                />
                                <span className="text-xs text-slate-300">Solo conversaciones inbound</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="conversation_filter"
                                    value="outbound_only"
                                    checked={filters.conversation_filter === 'outbound_only'}
                                    onChange={(e) => setFilters(prev => ({ 
                                        ...prev, 
                                        conversation_filter: e.target.value
                                    }))}
                                    className="w-4 h-4 text-green-500 bg-slate-950 border-slate-700 focus:ring-green-500 focus:ring-2"
                                />
                                <span className="text-xs text-slate-300">Solo conversaciones outbound</span>
                            </label>
                        </div>
                    </div>

                    {/* Tag Filter */}
                    <div>
                        <label className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                            <TagIcon size={14} />
                            Etiqueta
                        </label>
                        <select
                            value={filters.tag_id}
                            onChange={(e) => setFilters(prev => ({ ...prev, tag_id: e.target.value }))}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                        >
                            <option value="">Todas las etiquetas</option>
                            {tags.map(tag => (
                                <option key={tag.id} value={tag.id}>
                                    {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* En Gestión Filter */}
                    <div>
                        <label className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                            <Archive size={14} />
                            Estado de Gestión
                        </label>
                        <select
                            value={filters.en_gestion_filter}
                            onChange={(e) => setFilters(prev => ({ ...prev, en_gestion_filter: e.target.value }))}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
                        >
                            <option value="all">Todas las conversaciones</option>
                            <option value="true">✅ En gestión</option>
                            <option value="false">📦 Fuera de gestión</option>
                        </select>
                    </div>

                    {/* Search */}
                    <div>
                        <label className="text-xs text-slate-400 font-medium mb-2 flex items-center gap-1">
                            <Phone size={14} />
                            Buscar por teléfono/nombre
                        </label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            placeholder="Ej: 666123456"
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={handleApplyFilters}
                            className="flex-1 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Aplicar
                        </button>
                        <button
                            onClick={handleClearFilters}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="mt-4 pt-4 border-t border-slate-800">
                        <div className="text-xs text-slate-400">
                            <div className="flex justify-between mb-2">
                                <span>Total conversaciones:</span>
                                <span className="text-slate-200 font-semibold">{total}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Mostrando:</span>
                                <span className="text-slate-200 font-semibold">
                                    {offset + 1} - {Math.min(offset + limit, total)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Center: Conversations Table */}
                <div className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl flex flex-col overflow-hidden">
                    {/* Table Header */}
                    <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                        <h3 className="font-bold flex items-center gap-2">
                            <MessageSquare size={18} />
                            Conversaciones ({total})
                        </h3>
                    </div>

                    {/* Table Content */}
                    <div className="flex-1 overflow-y-auto">
                        {loading && (
                            <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 size={32} className="text-primary animate-spin mb-4" />
                                <span className="text-sm text-slate-400">Cargando conversaciones...</span>
                            </div>
                        )}

                        {error && (
                            <div className="flex flex-col items-center justify-center py-20">
                                <AlertCircle size={32} className="text-red-500 mb-4" />
                                <span className="text-sm text-red-400">{error}</span>
                            </div>
                        )}

                        {!loading && !error && conversations.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-20 opacity-30">
                                <MessageSquare size={40} className="text-slate-600 mb-4" />
                                <span className="text-sm text-slate-400">No se encontraron conversaciones</span>
                            </div>
                        )}

                        {!loading && !error && conversations.length > 0 && (
                            <table className="w-full">
                                <thead className="sticky top-0 bg-slate-950 border-b border-slate-800">
                                    <tr className="text-xs text-slate-400 uppercase tracking-wider">
                                        <th className="text-center p-3 w-12">
                                            <button
                                                onClick={handleSelectAll}
                                                className="text-slate-400 hover:text-slate-200 transition-colors"
                                            >
                                                {selectAll ? <CheckSquare size={18} /> : <Square size={18} />}
                                            </button>
                                        </th>
                                        <th 
                                            className="text-left p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('channel_name')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Canal</span>
                                                {sortBy === 'channel_name' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="text-left p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('contact_name')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Contacto</span>
                                                {sortBy === 'contact_name' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="text-left p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('agent_name')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Agente</span>
                                                {sortBy === 'agent_name' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="text-left p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('first_client_message_at')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Primera Actividad Cliente</span>
                                                {sortBy === 'first_client_message_at' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="text-left p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('last_client_message_at')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Última Actividad Cliente</span>
                                                {sortBy === 'last_client_message_at' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="text-left p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('last_imagina_message_at')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Última Actividad Imagina</span>
                                                {sortBy === 'last_imagina_message_at' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="text-center p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('message_count')}
                                        >
                                            <div className="flex items-center justify-center gap-1">
                                                <span>Mensajes</span>
                                                {sortBy === 'message_count' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="text-center p-3 cursor-pointer hover:text-slate-200 transition-colors"
                                            onClick={() => handleSort('en_gestion')}
                                        >
                                            <div className="flex items-center justify-center gap-1">
                                                <span>En Gestión</span>
                                                {sortBy === 'en_gestion' ? (
                                                    sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronsUpDown size={14} className="opacity-30" />
                                                )}
                                            </div>
                                        </th>
                                        <th className="text-center p-3">Tags</th>
                                        <th className="text-center p-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {conversations.map((conv) => (
                                        <tr
                                            key={conv.conversation_id}
                                            className={`border-b border-slate-800 hover:bg-slate-800/50 transition-colors cursor-pointer ${
                                                selectedConversation?.conversation_id === conv.conversation_id
                                                    ? 'bg-primary/10'
                                                    : ''
                                            }`}
                                            onClick={() => handleSelectConversation(conv)}
                                        >
                                            <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => toggleConversationSelection(conv.conversation_id)}
                                                    className="text-slate-400 hover:text-slate-200 transition-colors"
                                                >
                                                    {selectedConversations.includes(conv.conversation_id) ? (
                                                        <CheckSquare size={18} className="text-blue-400" />
                                                    ) : (
                                                        <Square size={18} />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="p-3">
                                                <ChannelBadge
                                                    color={conv.channel_color}
                                                    channel_name={conv.channel_name}
                                                    custom_name={conv.channel_custom_name}
                                                />
                                            </td>
                                            <td className="p-3">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-slate-200">
                                                        {conv.contact_name || 'Sin nombre'}
                                                    </span>
                                                    {conv.phone_number ? (
                                                        <CopyablePhone phone={conv.phone_number} textClassName="text-xs text-slate-500" />
                                                    ) : (
                                                        <span className="text-xs text-slate-500">-</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <span className="text-sm text-slate-300">
                                                    {conv.agent_name || conv.assigned_agent_id || conv.last_assigned_agent_id || '-'}
                                                </span>
                                            </td>
                                            <td className="p-3">
                                                <span className="text-xs text-slate-400">
                                                    {conv.first_client_message_at 
                                                        ? `${formatDate(conv.first_client_message_at)} (${getTimeAgo(conv.first_client_message_at)})`
                                                        : '-'}
                                                </span>
                                            </td>
                                            <td className="p-3">
                                                <span className="text-xs text-slate-400">
                                                    {conv.last_client_message_at 
                                                        ? `${formatDate(conv.last_client_message_at)} (${getTimeAgo(conv.last_client_message_at)})`
                                                        : '-'}
                                                </span>
                                            </td>
                                            <td className="p-3">
                                                <span className="text-xs text-slate-400">
                                                    {conv.last_imagina_message_at 
                                                        ? `${formatDate(conv.last_imagina_message_at)} (${getTimeAgo(conv.last_imagina_message_at)})`
                                                        : '-'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-800 text-xs text-slate-300 font-medium">
                                                    {conv.message_count || 0}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                {conv.tramite_id ? (
                                                    <button
                                                        onClick={(e) => handleToggleEnGestion(conv.conversation_id, conv.en_gestion !== false, e)}
                                                        disabled={isManagedReadOnly(conv) && !canResumeHumanAttention(conv)}
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all text-[10px] font-medium ${
                                                            conv.en_gestion !== false
                                                                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                                                                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                                        }`}
                                                        title={canResumeHumanAttention(conv) ? 'Recuperar atención humana'
                                                            : isManagedReadOnly(conv) ? getManagedReadOnlyMessage(conv)
                                                            : conv.en_gestion !== false ? "Sacar de gestión" : "Poner en gestión"}
                                                    >
                                                        {conv.en_gestion !== false ? <CheckCircle size={12} /> : <Archive size={12} />}
                                                        <span className="hidden xl:inline">
                                                            {conv.bot_return_pending ? 'Devolución al bot pendiente' : conv.en_gestion !== false ? "Sí" : "No"}
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-slate-500" title="Sin trámite vinculado">-</span>
                                                )}
                                            </td>
                                            <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                                {conv.tags && conv.tags.length > 0 && (
                                                    <div className="flex gap-1 flex-wrap justify-center">
                                                        {conv.tags.slice(0, 3).map(tag => (
                                                            <div
                                                                key={tag.id}
                                                                className="flex items-center text-[10px] px-2 py-0.5 rounded-full group"
                                                                style={{
                                                                    backgroundColor: `${tag.color}20`,
                                                                    color: tag.color,
                                                                    border: `1px solid ${tag.color}40`
                                                                }}
                                                            >
                                                                <span className="mr-1">{tag.name}</span>
                                                                <button
                                                                    onClick={(e) => handleRemoveTag(conv.conversation_id, tag.id, e)}
                                                                    className="opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity rounded-full p-0.5"
                                                                    style={{ backgroundColor: `${tag.color}40` }}
                                                                    title="Eliminar etiqueta"
                                                                >
                                                                    <X size={8} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                        {conv.tags.length > 3 && (
                                                            <span className="text-[10px] text-slate-500">
                                                                +{conv.tags.length - 3}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectConversation(conv);
                                                    }}
                                                    className="p-2 hover:bg-primary/20 rounded-lg transition-colors"
                                                    title="Ver mensajes"
                                                >
                                                    <Eye size={16} className="text-primary" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {total > limit && (
                        <div className="p-4 border-t border-slate-800 flex justify-between items-center bg-slate-950/50">
                            <button
                                onClick={handlePreviousPage}
                                disabled={offset === 0}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Anterior
                            </button>
                            <span className="text-sm text-slate-400">
                                Página {Math.floor(offset / limit) + 1} de {Math.ceil(total / limit)}
                            </span>
                            <button
                                onClick={handleNextPage}
                                disabled={offset + limit >= total}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Siguiente
                            </button>
                        </div>
                    )}
                </div>

                {/* Right Panel: Messages (Conditional) */}
                {selectedConversation && (
                    <div className="w-[500px] bg-slate-900 border border-slate-800 rounded-2xl flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                            <div className="flex-1">
                                <h3 className="font-bold text-sm">{selectedConversation.contact_name || 'Sin nombre'}</h3>
                                <CopyablePhone phone={selectedConversation.phone_number} textClassName="text-xs text-slate-400" />
                            </div>
                            <button
                                onClick={() => setSelectedConversation(null)}
                                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-950/20">
                            {loadingMessages && (
                                <div className="flex flex-col items-center justify-center py-10">
                                    <Loader2 size={24} className="text-primary animate-spin mb-3" />
                                    <span className="text-xs text-slate-400">Cargando mensajes...</span>
                                </div>
                            )}

                            {!loadingMessages && messages.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-10 opacity-30">
                                    <MessageSquare size={32} className="text-slate-600 mb-3" />
                                    <span className="text-xs text-slate-400">No hay mensajes</span>
                                </div>
                            )}

                            {!loadingMessages && messages.map((msg) => {
                                const isOutbound = msg.direction === 'outbound' || msg.direction === 'OUTBOUND';
                                return (
                                    <div
                                        key={msg.id}
                                        className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div
                                            className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                                                isOutbound
                                                    ? 'bg-primary/20 border border-primary/30'
                                                    : 'bg-slate-800 border border-slate-700'
                                            }`}
                                        >
                                            {msg.text_body && (
                                                <p className="text-sm text-slate-200 whitespace-pre-wrap break-words">
                                                    {msg.text_body}
                                                </p>
                                            )}
                                            {msg.attachments && msg.attachments.length > 0 && (
                                                <div className="mt-2 space-y-1.5">
                                                    {msg.attachments.map(att => (
                                                        <WhatsAppAttachmentItem 
                                                            key={att.id} 
                                                            att={att}
                                                            onDownload={handleDownloadAttachment}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            <span className="text-[10px] text-slate-500 mt-1 block">
                                                {formatDate(msg.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer - Read Only Note */}
                        <div className="p-3 border-t border-slate-800 bg-slate-950/50">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                <AlertCircle size={14} />
                                <span>Solo lectura - Vista de supervisión</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Exports Modal */}
            {showExportsModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-slate-800">
                            <div className="flex items-center gap-3">
                                <FileSpreadsheet className="text-emerald-400" size={24} />
                                <h2 className="text-xl font-bold">Exportaciones de Conversaciones</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => fetchExports()}
                                    disabled={loadingExports}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 rounded-lg text-xs transition-colors"
                                    title="Actualizar"
                                >
                                    <RefreshCw size={14} className={loadingExports ? 'animate-spin' : ''} />
                                    Actualizar
                                </button>
                                <button
                                    onClick={() => setShowExportsModal(false)}
                                    className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingExports ? (
                                <div className="flex flex-col items-center justify-center py-20">
                                    <Loader2 size={40} className="text-primary animate-spin mb-4" />
                                    <span className="text-sm text-slate-400">Cargando exportaciones...</span>
                                </div>
                            ) : exportsList.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 text-center">
                                    <FileSpreadsheet size={48} className="text-slate-600 mb-4" />
                                    <p className="text-slate-400 text-sm">
                                        No hay exportaciones todavía.
                                    </p>
                                    <p className="text-slate-500 text-xs mt-2">
                                        Haz clic en "Exportar" para crear tu primera exportación.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {exportsList.map((exp) => (
                                        <div
                                            key={exp.id}
                                            className="bg-slate-950 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    {/* Status Badge */}
                                                    <div className="mb-2 flex items-center gap-2">
                                                        {getStatusBadge(exp.status)}
                                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium bg-slate-800/60 text-slate-300 border-slate-700/50">
                                                            {exp.export_type === 'messages' ? <FileText size={12} /> : <FileSpreadsheet size={12} />}
                                                            {exp.export_type === 'messages' ? 'Mensajes' : 'Conversaciones'}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Filename */}
                                                    <p className="text-sm font-medium text-slate-200 mb-1 truncate">
                                                        {exp.filename || `export_${exp.id.substring(0, 8)}.xlsx`}
                                                    </p>
                                                    
                                                    {/* Info */}
                                                    <div className="flex items-center gap-4 text-xs text-slate-400">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar size={12} />
                                                            {new Date(exp.created_at).toLocaleString('es-ES', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                        
                                                        {exp.total_rows !== null && (
                                                            <span className="flex items-center gap-1">
                                                                <MessageSquare size={12} />
                                                                {exp.total_rows} {exp.export_type === 'messages' ? 'mensajes' : 'conversaciones'}
                                                            </span>
                                                        )}
                                                        
                                                        {exp.completed_at && (
                                                            <span className="flex items-center gap-1">
                                                                <Clock size={12} />
                                                                Completado: {new Date(exp.completed_at).toLocaleTimeString('es-ES', {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Error Message */}
                                                    {exp.status === 'failed' && exp.error_message && (
                                                        <div className="mt-2 text-xs text-red-400 bg-red-900/20 border border-red-900/30 rounded px-2 py-1">
                                                            Error: {exp.error_message}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Actions */}
                                                <div className="flex items-center gap-2 ml-4">
                                                    {exp.status === 'completed' && (
                                                        <button
                                                            onClick={() => handleDownloadExport(exp.id, exp.filename)}
                                                            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-600/30 rounded-lg text-xs transition-colors"
                                                        >
                                                            <Download size={14} />
                                                            Descargar
                                                        </button>
                                                    )}
                                                    
                                                    <button
                                                        onClick={() => handleDeleteExport(exp.id)}
                                                        className="p-2 hover:bg-red-900/30 text-red-400 rounded-lg transition-colors"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-slate-800 bg-slate-900/50">
                            <div className="flex items-center justify-end text-xs text-slate-400">
                                <button
                                    onClick={() => setShowExportsModal(false)}
                                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notifications */}
            <div className="fixed top-4 right-4 z-[60] space-y-3 pointer-events-none">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className="pointer-events-auto animate-slide-in-right bg-slate-900 border-2 rounded-xl shadow-2xl overflow-hidden min-w-[320px] max-w-md"
                        style={{
                            borderColor: toast.type === 'success' ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)',
                            boxShadow: toast.type === 'success' 
                                ? '0 20px 25px -5px rgba(34, 197, 94, 0.3), 0 8px 10px -6px rgba(34, 197, 94, 0.3)' 
                                : '0 20px 25px -5px rgba(239, 68, 68, 0.3), 0 8px 10px -6px rgba(239, 68, 68, 0.3)'
                        }}
                    >
                        <div className="flex items-start gap-3 p-4">
                            <div className={`flex-shrink-0 mt-0.5 ${toast.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                                {toast.type === 'success' ? (
                                    <CheckCircle2 size={24} className="animate-scale-in" />
                                ) : (
                                    <AlertCircle size={24} className="animate-scale-in" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-sm mb-1 ${toast.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                                    {toast.title}
                                </p>
                                <p className="text-xs text-slate-400 leading-relaxed">
                                    {toast.message}
                                </p>
                            </div>
                            <button
                                onClick={() => dismissToast(toast.id)}
                                className="flex-shrink-0 p-1 hover:bg-slate-800 rounded-lg transition-colors text-slate-500 hover:text-slate-300"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="h-1 bg-slate-800 overflow-hidden">
                            <div
                                className={`h-full animate-shrink-width ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}
                                style={{ animationDuration: '5s' }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WhatsAppSupervisorView;
